import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useRef } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import {
  DocumentPreview,
  downloadBlob,
  printHtmlAsPdf,
  toPrintDocument
} from 'utils/printHtmlAsPdf';

type HtmlPdfPreviewModalProps = {
  show: boolean;
  onHide: () => void;
  title: string;
  preview: DocumentPreview | null;
  isLoading?: boolean;
};

const HtmlPdfPreviewModal = ({
  show,
  onHide,
  title,
  preview,
  isLoading = false
}: HtmlPdfPreviewModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const documentHtml = useMemo(
    () =>
      preview?.kind === 'html'
        ? toPrintDocument(preview.html, preview.fileName)
        : '',
    [preview]
  );

  useEffect(() => {
    if (!show || preview?.kind !== 'html') return;
    const iframe = iframeRef.current;
    if (!iframe || !documentHtml) return;
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(documentHtml);
    doc.close();
  }, [show, documentHtml, preview?.kind]);

  const handleDownload = () => {
    if (!preview) return;

    if (preview.kind === 'pdf') {
      downloadBlob(preview.blob, preview.fileName);
      return;
    }

    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow) {
      iframeWindow.focus();
      iframeWindow.print();
      return;
    }

    printHtmlAsPdf(preview.html, preview.fileName);
  };

  const ready = !!preview && !isLoading;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ minHeight: '70vh' }}>
        {!ready ? (
          <div className="d-flex justify-content-center align-items-center py-10">
            <Spinner animation="border" role="status" className="me-2" />
            Generating preview...
          </div>
        ) : preview.kind === 'pdf' ? (
          <iframe
            src={preview.pdfUrl}
            title={title}
            style={{
              width: '100%',
              height: '70vh',
              border: 0,
              background: '#fff'
            }}
          />
        ) : (
          <iframe
            ref={iframeRef}
            title={title}
            style={{
              width: '100%',
              height: '70vh',
              border: 0,
              background: '#fff'
            }}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <small className="text-body-tertiary">
          {preview?.kind === 'pdf'
            ? 'Preview ready. Use Download PDF to save the file.'
            : 'Choose "Save as PDF" in the print dialog to download.'}
        </small>
        <div className="d-flex gap-2">
          <Button variant="phoenix-secondary" onClick={onHide}>
            Close
          </Button>
          <Button variant="primary" onClick={handleDownload} disabled={!ready}>
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Download PDF
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default HtmlPdfPreviewModal;
